import React, { useState, useContext, useEffect } from 'react';
import { GroupGame, ImageEdits, defaultImageEdits } from '../types';
import { useAppContext } from '../AppContext';
import CameraModal from './CameraModal';
import { PageContext } from '../PageContext';
import { useAuth } from '../AuthContext';
import { uploadFile, base64ToBlob } from '../storageUtils';

interface CreateGameModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (groupData: Omit<GroupGame, 'id' | 'creatorId' | 'members' | 'admins' | 'scores' | 'solvedPuzzles' | 'joinRequests'>) => void;
    triggerRect: DOMRect | null;
}

const CreateGameModal: React.FC<CreateGameModalProps> = ({ isOpen, onClose, onCreate }) => {
    const pageContext = useContext(PageContext);
    const { userProfile } = useAuth();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [privacy, setPrivacy] = useState<'public' | 'private' | 'ask-to-join'>('public');
    const [imageUrl, setImageUrl] = useState('');
    const [imageEdits, setImageEdits] = useState<ImageEdits>(defaultImageEdits);
    const [isCameraOpen, setCameraOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Reset form when modal opens
            setName('');
            setDescription('');
            setPrivacy('public');
            setImageUrl('');
            setImageEdits(defaultImageEdits);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Group name is required.');
            return;
        }
        onCreate({
            name,
            description,
            privacy,
            puzzles: [],
            imageUrl: imageUrl || 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=2070&auto=format&fit=crop',
            imageEdits,
        });
        onClose();
    };

    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0] && userProfile) {
            const file = e.target.files[0];
            setIsUploading(true);
            try {
                const downloadURL = await uploadFile(file, `group-images/${userProfile.uid}`);
                setImageUrl(downloadURL);
                setImageEdits(defaultImageEdits);
                pageContext?.launchImageEditor('post', downloadURL, defaultImageEdits, 'square', setImageEdits, null);
            } catch (error) {
                console.error("Upload failed:", error);
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    const handleImageFromCamera = async (base64Image: string, mimeType: string) => {
        if (!userProfile) return;
        setCameraOpen(false);
        setIsUploading(true);
        try {
            const blob = base64ToBlob(base64Image, mimeType);
            const downloadURL = await uploadFile(blob, `group-images/${userProfile.uid}`);
            setImageUrl(downloadURL);
            setImageEdits(defaultImageEdits);
            pageContext?.launchImageEditor('post', downloadURL, defaultImageEdits, 'square', setImageEdits, null);
        } catch (error) {
            console.error("Upload failed:", error);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center sm:p-4 animate-fade-in" onClick={onClose}>
                <div 
                    className="bg-[var(--primary-dark-purple)] text-white w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:rounded-xl shadow-2xl max-w-lg flex flex-col border border-white/20 overflow-hidden transition-all" 
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-4 border-b border-white/20 shrink-0 flex justify-between items-center bg-[var(--primary-dark-purple)]">
                        <h2 className="text-xl font-bold aurora-text">Create a New Group</h2>
                        <button onClick={onClose} className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors">
                            <i className="ph-duotone ph-x text-2xl"></i>
                        </button>
                    </div>
                    
                    {/* Content - Grows to fill space */}
                    <div className="p-4 space-y-3 overflow-y-auto custom-scrollbar flex-grow bg-[var(--primary-dark-purple)]">
                        <form id="create-group-form" onSubmit={handleSubmit} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-300">Group Image (Optional)</label>
                                <div className="mt-1 flex justify-center px-6 pt-3 pb-3 border-2 border-white/30 border-dashed rounded-md bg-white/5">
                                    {isUploading ? (
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="spinner-white"></div>
                                            <p className="mt-2 text-sm text-gray-300">Uploading...</p>
                                        </div>
                                    ) : imageUrl ? (
                                        <div className="relative group">
                                            <img src={imageUrl} alt="Preview" className="max-h-32 rounded-lg shadow-md" />
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                                <button type="button" onClick={() => { setImageUrl(''); setImageEdits(defaultImageEdits); }} className="text-white font-bold py-1 px-3 rounded-lg bg-red-500/80 hover:bg-red-600 transition-colors">Change</button>
                                                <button type="button" onClick={() => pageContext?.launchImageEditor('post', imageUrl, imageEdits, 'square', setImageEdits, null)} className="ml-2 text-white font-bold py-1 px-3 rounded-lg bg-blue-500/80 hover:bg-blue-600 transition-colors">Edit</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-1 text-center">
                                            <svg className="mx-auto h-10 w-10 text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <div className="flex flex-col sm:flex-row justify-center items-center gap-2 text-sm text-gray-400">
                                                <label htmlFor="group-file-upload" className="relative cursor-pointer bg-transparent rounded-md font-medium text-[var(--accent-pink)] hover:text-pink-400 focus-within:outline-none">
                                                    <span>Upload a file</span>
                                                    <input id="group-file-upload" name="group-file-upload" type="file" className="sr-only" onChange={handleImageSelect} accept="image/*" />
                                                </label>
                                                <p className="px-1 text-gray-400">or</p>
                                                <button type="button" onClick={() => setCameraOpen(true)} className="relative cursor-pointer bg-transparent rounded-md font-medium text-[var(--accent-pink)] hover:text-pink-400">
                                                    Take a photo
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="groupName" className="block text-sm font-medium text-gray-300">Group Name</label>
                                <input type="text" id="groupName" value={name} onChange={e => setName(e.target.value)} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)] p-2" required />
                            </div>
                            <div>
                                <label htmlFor="groupDesc" className="block text-sm font-medium text-gray-300">Description</label>
                                <textarea id="groupDesc" value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)] p-2" />
                            </div>
                            <div>
                                 <label className="block text-sm font-medium text-gray-300">Privacy</label>
                                 <div className="flex rounded-md shadow-sm mt-1 border border-white/20 overflow-hidden">
                                    <button type="button" onClick={() => setPrivacy('public')} className={`flex-1 py-2 px-4 text-sm font-bold transition-colors ${privacy === 'public' ? 'bg-white text-[var(--primary-dark-purple)]' : 'bg-transparent text-gray-300 hover:bg-white/10'}`}>Public</button>
                                    <button type="button" onClick={() => setPrivacy('private')} className={`flex-1 py-2 px-4 text-sm font-bold border-l border-r border-white/20 transition-colors ${privacy === 'private' ? 'bg-white text-[var(--primary-dark-purple)]' : 'bg-transparent text-gray-300 hover:bg-white/10'}`}>Private</button>
                                    <button type="button" onClick={() => setPrivacy('ask-to-join')} className={`flex-1 py-2 px-4 text-sm font-bold transition-colors ${privacy === 'ask-to-join' ? 'bg-white text-[var(--primary-dark-purple)]' : 'bg-transparent text-gray-300 hover:bg-white/10'}`}>Ask to Join</button>
                                </div>
                            </div>

                            {/* Buttons Moved Inside Scrollable Area */}
                            <div className="pt-2 flex justify-end space-x-3 pb-[max(2rem,env(safe-area-inset-bottom))]">
                                <button type="button" onClick={onClose} className="bg-transparent border border-white/50 text-white font-bold py-3 px-6 rounded-lg hover:bg-white/10 transition-colors">Cancel</button>
                                <button type="submit" disabled={isUploading} className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-3 px-8 rounded-lg disabled:bg-gray-500 shadow-lg hover:opacity-90 transition-opacity">
                                    {isUploading ? 'Uploading...' : 'Create Group'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <CameraModal
                isOpen={isCameraOpen}
                onClose={() => setCameraOpen(false)}
                onImageSelected={handleImageFromCamera}
            />
        </>
    );
};

export default CreateGameModal;